import React, { memo, useMemo } from "react";
import { TASKS_BOARD_ID } from "../../constants/columnIds";

const NewTaskPopup = memo(function NewTaskPopup({
  onClose,
  newTaskTitle,
  setNewTaskTitle,
  newTaskDescription,
  setNewTaskDescription,
  newTaskDate,
  setNewTaskDate,
  newTaskPerson,
  setNewTaskPerson,
  newTaskProperty,
  setNewTaskProperty,
  newTaskPropertySearch,
  setNewTaskPropertySearch,
  newTaskTimeTracking,
  setNewTaskTimeTracking,
  availablePeople,
  availableProperties,
  personColumnId,
  personColumnIdMap,
  handleCreateTask,
  creatingTask,
}) {
  // Memoize filtered properties to avoid recalculation on every render
  const filteredProperties = useMemo(() => {
    if (!newTaskPropertySearch) return [];
    const searchLower = newTaskPropertySearch.toLowerCase();
    return availableProperties
      .filter((prop) => prop.name.toLowerCase().includes(searchLower))
      .slice(0, 10);
  }, [availableProperties, newTaskPropertySearch]);

  const hasNoProperties = useMemo(() => {
    if (!newTaskPropertySearch) return false;
    const searchLower = newTaskPropertySearch.toLowerCase();
    return availableProperties.filter((prop) =>
      prop.name.toLowerCase().includes(searchLower)
    ).length === 0;
  }, [availableProperties, newTaskPropertySearch]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 8,
          padding: 24,
          width: "100%",
          maxWidth: 500,
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Create New Task</h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
              color: "#676879",
              padding: 0,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                color: "#676879",
                marginBottom: 6,
                fontWeight: 500,
              }}
            >
              Title <span style={{ color: "#d83a52" }}>*</span>
            </label>
            <input
              type="text"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              placeholder="Enter task title..."
              style={{
                width: "100%",
                padding: 8,
                border: "1px solid #e6e9ef",
                borderRadius: 4,
                fontSize: 14,
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                color: "#676879",
                marginBottom: 6,
                fontWeight: 500,
              }}
            >
              Description
            </label>
            <textarea
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
              placeholder="Enter task description..."
              rows={4}
              style={{
                width: "100%",
                padding: 8,
                border: "1px solid #e6e9ef",
                borderRadius: 4,
                fontSize: 14,
                fontFamily: "inherit",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                color: "#676879",
                marginBottom: 6,
                fontWeight: 500,
              }}
            >
              Date <span style={{ color: "#d83a52" }}>*</span>
            </label>
            <input
              type="date"
              value={newTaskDate}
              onChange={(e) => setNewTaskDate(e.target.value)}
              style={{
                width: "100%",
                padding: 8,
                border: "1px solid #e6e9ef",
                borderRadius: 4,
                fontSize: 14,
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>

          {availablePeople && availablePeople.length > 0 && (
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#676879",
                  marginBottom: 6,
                  fontWeight: 500,
                }}
              >
                Person
              </label>
              <select
                value={newTaskPerson}
                onChange={(e) => setNewTaskPerson(e.target.value)}
                style={{
                  width: "100%",
                  padding: 8,
                  border: "1px solid #e6e9ef",
                  borderRadius: 4,
                  background: "#ffffff",
                  color: "#323338",
                  cursor: "pointer",
                  fontSize: 14,
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              >
                <option value="">Select a person...</option>
                {availablePeople.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label
              style={{
                display: "block",
                fontSize: 13,
                color: "#676879",
                marginBottom: 6,
                fontWeight: 500,
              }}
            >
              Property <span style={{ color: "#d83a52" }}>*</span>
            </label>
            {newTaskProperty ? (
              <div
                style={{
                  padding: 8,
                  background: "#f4f5f8",
                  borderRadius: 4,
                  fontSize: 14,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  {availableProperties.find((p) => p.id === newTaskProperty)?.name ||
                    "Selected property"}
                </span>
                <button
                  onClick={() => {
                    setNewTaskProperty("");
                    setNewTaskPropertySearch("");
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#676879",
                    cursor: "pointer",
                    fontSize: 18,
                    padding: 0,
                    width: 20,
                    height: 20,
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  value={newTaskPropertySearch}
                  onChange={(e) => {
                    setNewTaskPropertySearch(e.target.value);
                  }}
                  placeholder="Search property..."
                  style={{
                    width: "100%",
                    padding: 8,
                    border: "1px solid #e6e9ef",
                    borderRadius: 4,
                    fontSize: 14,
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                />
                {newTaskPropertySearch && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#ffffff",
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      maxHeight: 200,
                      overflowY: "auto",
                      zIndex: 10,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      marginTop: 4,
                    }}
                  >
                    {filteredProperties.map((prop) => (
                        <div
                          key={prop.id}
                          onClick={() => {
                            setNewTaskProperty(prop.id);
                            setNewTaskPropertySearch(prop.name);
                          }}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: 14,
                            borderBottom: "1px solid #f4f5f8",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "#f4f5f8";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "#ffffff";
                          }}
                        >
                          {prop.name}
                        </div>
                      ))}
                    {hasNoProperties && (
                      <div
                        style={{
                          padding: "8px 12px",
                          fontSize: 14,
                          color: "#676879",
                          fontStyle: "italic",
                        }}
                      >
                        No properties found
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "#676879",
                marginBottom: 6,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newTaskTimeTracking}
                onChange={(e) => setNewTaskTimeTracking(e.target.checked)}
                style={{
                  width: 16,
                  height: 16,
                  cursor: "pointer",
                }}
              />
              Track time for this task
            </label>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 24,
            paddingTop: 20,
            borderTop: "1px solid #e6e9ef",
          }}
        >
          <button
            onClick={onClose}
            disabled={creatingTask}
            style={{
              padding: "8px 16px",
              border: "1px solid #e6e9ef",
              borderRadius: 4,
              background: "#ffffff",
              color: "#323338",
              cursor: creatingTask ? "not-allowed" : "pointer",
              fontSize: 14,
              opacity: creatingTask ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreateTask}
            disabled={
              creatingTask || 
              !newTaskTitle.trim() || 
              !newTaskDate || 
              !newTaskProperty
            }
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: 4,
              background:
                creatingTask || 
                !newTaskTitle.trim() || 
                !newTaskDate || 
                !newTaskProperty
                  ? "#a1a4b5"
                  : "#00c875",
              color: "#ffffff",
              cursor:
                creatingTask || 
                !newTaskTitle.trim() || 
                !newTaskDate || 
                !newTaskProperty
                  ? "not-allowed"
                  : "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {creatingTask ? "Creating..." : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
});

export default NewTaskPopup;
